// frontend/src/components/public/blocks/BrandVisibilityBlock.tsx
import React, { useState } from 'react';
import { Box, Typography, Tabs, Tab, Paper, IconButton } from '@mui/material';
import {
  Computer as ComputerIcon,
  Print as PrintIcon,
  Business as BusinessIcon,
  Article as ArticleIcon,
  ArrowForward as ArrowIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';

interface BrandVisibilityBlockProps {
  data: {
    categories: Array<{
      name: 'digital' | 'print' | 'venue' | 'press';
      label: string;
      images: Array<{
        url: string;
        caption: string;
      }>;
      metricCallout: string;
      description: string;
      anchorLinkText?: string;
      anchorLinkBlockId?: string;
    }>;
    logoSpecs: string;
  };
  theme?: {
    primaryColor: string;
    accentColor: string;
  };
}

const BrandVisibilityBlock: React.FC<BrandVisibilityBlockProps> = ({ data, theme }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [carouselIndexes, setCarouselIndexes] = useState<Record<number, number>>({});

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleCarouselNext = (categoryIndex: number, maxIndex: number) => {
    setCarouselIndexes(prev => ({
      ...prev,
      [categoryIndex]: ((prev[categoryIndex] || 0) + 1) % (maxIndex + 1)
    }));
  };

  const handleCarouselPrev = (categoryIndex: number, maxIndex: number) => {
    setCarouselIndexes(prev => ({
      ...prev,
      [categoryIndex]: ((prev[categoryIndex] || 0) - 1 + (maxIndex + 1)) % (maxIndex + 1)
    }));
  };

  const getCategoryIcon = (name: string) => {
    switch (name) {
      case 'digital': return <ComputerIcon />;
      case 'print': return <PrintIcon />;
      case 'venue': return <BusinessIcon />;
      case 'press': return <ArticleIcon />;
      default: return <ComputerIcon />;
    }
  };

  const handleAnchorClick = (blockId?: string) => {
    if (!blockId) return;
    const element = document.getElementById(blockId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <Box
      id="brand-visibility"
      sx={{
        py: 8,
        px: 3,
        bgcolor: '#f9f9f9'
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: theme?.primaryColor || '#2C3E50',
              mb: 1
            }}
          >
            Where Your Brand Appears
          </Typography>
          <Typography variant="body1" sx={{ color: '#666' }}>
            Comprehensive visibility across all our channels
          </Typography>
        </Box>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                fontSize: '1rem',
                color: '#666',
                '&.Mui-selected': {
                  color: theme?.primaryColor || '#3A9188'
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: theme?.primaryColor || '#3A9188',
                height: 3
              }
            }}
          >
            {data.categories.map((category, index) => (
              <Tab
                key={index}
                icon={getCategoryIcon(category.name)}
                label={category.label}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        {/* Tab Content */}
        {data.categories.map((category, categoryIndex) => (
          <Box
            key={categoryIndex}
            sx={{
              display: activeTab === categoryIndex ? 'block' : 'none'
            }}
          >
            {/* Image Carousel */}
            {category.images.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                >
                  {/* Current Image */}
                  {category.images.map((image, imageIndex) => (
                    <Box
                      key={imageIndex}
                      sx={{
                        display: (carouselIndexes[categoryIndex] || 0) === imageIndex ? 'block' : 'none'
                      }}
                    >
                      {image.url ? (
                        <>
                          <img
                            src={image.url}
                            alt={image.caption}
                            style={{
                              width: '100%',
                              height: '500px',
                              objectFit: 'cover',
                              display: 'block'
                            }}
                          />
                          {/* Caption Overlay */}
                          {image.caption && (
                            <Box
                              sx={{
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                bgcolor: 'rgba(0,0,0,0.7)',
                                color: '#fff',
                                py: 2,
                                px: 3
                              }}
                            >
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {image.caption}
                              </Typography>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Box
                          sx={{
                            width: '100%',
                            height: '500px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#e0e0e0'
                          }}
                        >
                          <Typography variant="h6" sx={{ color: '#999' }}>
                            No image uploaded
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}

                  {/* Navigation Arrows */}
                  {category.images.length > 1 && (
                    <>
                      <IconButton
                        onClick={() => handleCarouselPrev(categoryIndex, category.images.length - 1)}
                        sx={{
                          position: 'absolute',
                          left: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255,255,255,0.9)',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,1)'
                          }
                        }}
                      >
                        <ChevronLeftIcon sx={{ fontSize: 32 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleCarouselNext(categoryIndex, category.images.length - 1)}
                        sx={{
                          position: 'absolute',
                          right: 16,
                          top: '50%',
                          transform: 'translateY(-50%)',
                          bgcolor: 'rgba(255,255,255,0.9)',
                          '&:hover': {
                            bgcolor: 'rgba(255,255,255,1)'
                          }
                        }}
                      >
                        <ChevronRightIcon sx={{ fontSize: 32 }} />
                      </IconButton>
                    </>
                  )}

                  {/* Carousel Dots */}
                  {category.images.length > 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: 80,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        display: 'flex',
                        gap: 1
                      }}
                    >
                      {category.images.map((_, dotIndex) => (
                        <Box
                          key={dotIndex}
                          onClick={() => setCarouselIndexes(prev => ({ ...prev, [categoryIndex]: dotIndex }))}
                          sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: (carouselIndexes[categoryIndex] || 0) === dotIndex
                              ? theme?.primaryColor || '#3A9188'
                              : 'rgba(255,255,255,0.5)',
                            cursor: 'pointer',
                            transition: 'all 0.3s',
                            '&:hover': {
                              bgcolor: theme?.primaryColor || '#3A9188',
                              transform: 'scale(1.2)'
                            }
                          }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}

            {/* Metric Callout */}
            {category.metricCallout && (
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  mb: 3,
                  bgcolor: theme?.primaryColor || '#3A9188',
                  color: '#fff',
                  textAlign: 'center',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  📊 {category.metricCallout}
                </Typography>
              </Paper>
            )}

            {/* Description */}
            {category.description && (
              <Paper
                elevation={1}
                sx={{
                  p: 4,
                  mb: 3,
                  bgcolor: '#fff',
                  borderRadius: 2
                }}
              >
                <div
                  dangerouslySetInnerHTML={{ __html: category.description }}
                  style={{
                    color: '#2C3E50',
                    lineHeight: 1.8
                  }}
                />
              </Paper>
            )}
{/* Anchor Link */}
{category.anchorLinkText && (
  <Box
    onClick={() => handleAnchorClick(category.anchorLinkBlockId)}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 1,
      color: theme?.primaryColor || '#3A9188',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.2s',
      '&:hover': {
        gap: 2,
        textDecoration: 'underline'
      }
    }}
  >
    <Typography 
      variant="body1"
      sx={{ color: 'inherit' }}
    >
      {category.anchorLinkText}
    </Typography>
    <ArrowIcon />
  </Box>
)}
          </Box>
        ))}

        {/* Logo Specs */}
        {data.logoSpecs && (
          <Paper
            elevation={1}
            sx={{
              p: 4,
              mt: 6,
              bgcolor: '#fff',
              borderRadius: 2,
              borderLeft: `4px solid ${theme?.primaryColor || '#3A9188'}`
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: theme?.primaryColor || '#2C3E50',
                mb: 2
              }}
            >
              Logo Specifications
            </Typography>
            <Typography variant="body1" sx={{ color: '#666', lineHeight: 1.8 }}>
              {data.logoSpecs}
            </Typography>
          </Paper>
        )}
      </Box>
    </Box>
  );
};

export default BrandVisibilityBlock;